import { parseISO } from 'date-fns';
import { maxBy } from 'lodash';
import { useState, createContext, useEffect, useCallback } from 'react';
import { useOffers } from '../../offers/AllOfferCards/useOffers';
import { useHealthInterests } from '../../patient-accounts/health-interests/useHealthInterests';
import { useUserData } from '../UserProvider/UserProvider';
import { api3Call } from '../schema/api3/client';
import { account } from '../schema/api3/endpoints';
import { Api3CTA, QueryKeys } from '../schema/api3/types/Api3CTAs';
import { Api3CTAKeys } from '../schema/api3/types/Api3CTAs';
import { useFetch } from '../services/NetworkService';
import { ChildrenOnly } from '../types/ChildrenOnly';

export interface CTA {
  showHealthInterestsCTA?: boolean;
  showMctCTA?: boolean;
  showNewOfferAddedCTA?: boolean;
  showPrivacyPreferencesCTA?: boolean;
  showTelehealthCTA?: boolean;
}

export interface ContextData {
  cta: CTA;
  updateCta: (ctas: CTA) => void;
}

const defaultState: ContextData = {
  cta: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateCta: (_ctas: CTA) => {},
};

export const CTAContext = createContext(defaultState);

export const CTAProvider = ({ children }: ChildrenOnly) => {
  const [cta, setCta] = useState<Api3CTA>();
  const [parsedCta, setParsedCta] = useState<CTA>({});

  const fetch = useFetch();
  const { data: user, loading: loadingUser } = useUserData();

  const { userHealthInterests, loading: loadingUserHealthInterests } = useHealthInterests({
    skip: loadingUser || !user,
  });

  const { data: rawOffers } = useOffers({
    skip: loadingUser || !user,
  });

  const newestOffer = maxBy(rawOffers, offer => parseISO(offer.createdAt));

  useEffect(() => {
    if (!user || !newestOffer) {
      return;
    }

    const keys = [...QueryKeys, `OFFER${newestOffer.id}`];

    api3Call(fetch, account.getCTAs(keys))
      .then(result => setCta(result))
      .catch(error => console.error('Could not fetch bookings data', error));
  }, [fetch, loadingUser, newestOffer, user]);

  useEffect(() => {
    if (!cta) {
      return;
    }

    const mapKeys2Cta = (keys: Api3CTA): CTA => {
      const hasNoHealthInterests = loadingUser
        ? false
        : userHealthInterests.filter(item => item.subscribed).length === 0;

      const hasNoMctPhi = loadingUserHealthInterests ? false : !user?.phi?.isMct;

      const optedIntoPrivacyPreferences =
        user?.privacyPreferences &&
        Object.values(user.privacyPreferences).some(preference => preference);

      return {
        showPrivacyPreferencesCTA:
          !optedIntoPrivacyPreferences && !keys.data.viewedPrivacyPreferencesPage,
        showHealthInterestsCTA: hasNoHealthInterests && !keys.data.viewedInterestsPage,
        showMctCTA: hasNoMctPhi && !keys.data.viewedMctPhi,
        showNewOfferAddedCTA: newestOffer ? !keys.data[`OFFER${newestOffer.id}`] : false,
        showTelehealthCTA: !keys.data.viewedTelehealthPage,
      };
    };

    setParsedCta(mapKeys2Cta(cta));
  }, [
    cta,
    loadingUser,
    loadingUserHealthInterests,
    newestOffer,
    rawOffers,
    user,
    userHealthInterests,
  ]);

  const updateCta = useCallback(
    (ctas: CTA): void => {
      const mapCta2Keys = (keys: CTA): Api3CTAKeys => {
        const ctaKeys: Api3CTAKeys = {
          viewedInterestsPage:
            keys.showHealthInterestsCTA === undefined ? undefined : !keys.showTelehealthCTA,
          viewedMctPhi: keys.showMctCTA === undefined ? undefined : !keys.showMctCTA,
          viewedPrivacyPreferencesPage:
            keys.showPrivacyPreferencesCTA === undefined
              ? undefined
              : !keys.showPrivacyPreferencesCTA,
          viewedTelehealthPage:
            keys.showTelehealthCTA === undefined ? undefined : !keys.showTelehealthCTA,
        };

        if (newestOffer && keys.showNewOfferAddedCTA !== undefined) {
          ctaKeys[`OFFER${newestOffer.id}`] = !keys.showNewOfferAddedCTA;
        }

        // Delete undefined keys
        Object.keys(ctaKeys).forEach(key => ctaKeys[key] === undefined && delete ctaKeys[key]);

        return ctaKeys;
      };

      api3Call(fetch, account.saveCTAs(mapCta2Keys(ctas)))
        .then(() => setParsedCta({ ...parsedCta, ...ctas }))
        .catch(error => console.error('Could not fetch bookings data', error));
    },
    [fetch, newestOffer, parsedCta],
  );

  return (
    <CTAContext.Provider value={{ cta: parsedCta, updateCta }}>{children}</CTAContext.Provider>
  );
};
